import React from "react";
import BottomSection from "../components/BottomSection";
import Header from "../components/Header";

const PrivacyPolicy = () => {
  return (
    <div>
      <Header />
      <div className='max-w-3xl mx-auto my-10 px-6'>
        <h1 className='text-3xl font-bold text-center text-gray-800'>
          Privacy Policy
        </h1>
        <p className='text-gray-600 mt-4'>
          <b>Effective Date:</b> July 20, 2026
          <br />
          <b>Last Updated:</b> July 20, 2026
        </p>
        <p className='text-gray-600 mt-4'>
          Arptix Solutions Inc. ("Arptix", "we", "us", or "our") owns and
          operates the HomeOwner mobile application, website, and related
          services (collectively, the "Service"). Your privacy and the
          protection of your personal information are fundamental to our
          operations.
        </p>
        <p className='text-gray-600 mt-4'>
          This Privacy Policy explains what personal information we collect, how
          we use it, who we share it with, and the rights you have regarding
          your data under applicable privacy laws, including the Personal
          Information Protection and Electronic Documents Act (PIPEDA).
        </p>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            1. Information We Collect
          </h2>
          <p className='text-gray-600 mt-2'>
            We collect information you provide directly to us, information
            generated automatically during your use of the Service, and
            information from third-party integrations.
          </p>

          <div className='ml-5 mt-4'>
            <h3 className='text-xl font-semibold'>
              1.1 User Account & Profile Data
            </h3>
            <p className='text-gray-600 mt-2'>
              When you register for an account or use the Service, we collect:
            </p>
            <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
              <li>
                <b>Contact Information:</b> Full name, email address, and phone
                number.
              </li>
              <li>
                <b>Authentication Data:</b> Passwords (securely hashed). If you
                choose to log in using third-party services like Apple or
                Google, we receive basic profile information (such as your name
                and email address) permitted by your settings with those
                providers.
              </li>
              <li>
                <b>Journey Data (where applicable):</b> If you use home-journey
                features, we may collect information such as your current
                address, new address, selling address, property type, moving
                dates, and related preferences.
              </li>
            </ul>
          </div>

          <div className='ml-5 mt-4'>
            <h3 className='text-xl font-semibold'>
              1.2 Rental, Financial & Tenant Data (User-Provided)
            </h3>
            <p className='text-gray-600 mt-2'>
              If you utilize our property management features, you may input
              data regarding your rental properties, finances, and tenants. In
              this capacity, <b>you act as the Data Controller</b>, and we act
              as the Data Processor. This data includes:
            </p>
            <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
              <li>
                <b>Property Details:</b> Property addresses, unit numbers, and
                property types.
              </li>
              <li>
                <b>Tenant Information:</b> Tenant full names, email addresses,
                and phone numbers.
              </li>
              <li>
                <b>Lease & Payment Details:</b> Rent amounts, payment
                frequencies, lease start and end dates, security deposits,
                utility inclusions, payment histories, overdue balances, and
                generated rent receipts.
              </li>
              <li>
                <b>Expense & Tax Records:</b> Property expenses you log
                (amounts, categories, dates, and vendor names), mortgage payment
                and interest figures you enter, property tax and insurance
                amounts, and the income and expense summaries or year-end tax
                reports generated from this data.
              </li>
              <li>
                <b>Management Records:</b> Maintenance schedules, custom notes,
                and logged tenancy issues (e.g., lease violations, property
                damage).
              </li>
            </ul>
          </div>

          <div className='ml-5 mt-4'>
            <h3 className='text-xl font-semibold'>1.3 Uploaded Documents</h3>
            <p className='text-gray-600 mt-2'>
              The Service allows you to upload, tag, and store documents (e.g.,
              lease agreements, insurance policies, bills, expense receipts,
              notices). These documents are encrypted at rest.{" "}
              <b>You are responsible</b> for ensuring you have the legal right
              to upload these documents. We strongly advise against uploading
              documents containing highly sensitive, unredacted personal
              information (such as Social Insurance Numbers or bank account
              details) unless strictly necessary for your records.
            </p>
          </div>

          <div className='ml-5 mt-4'>
            <h3 className='text-xl font-semibold'>
              1.4 Purchase & Billing Information
            </h3>
            <p className='text-gray-600 mt-2'>
              The Service includes a one-time purchase to unlock the app and
              optional in-app purchases. All payments are processed by the Apple
              App Store or Google Play, not by us.
            </p>
            <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
              <li>
                <b>
                  We do not collect, receive, or store your payment card or bank
                  details.
                </b>{" "}
                Those are handled entirely by Apple or Google.
              </li>
              <li>
                We receive and store purchase-related information such as
                transaction identifiers, purchase receipts or tokens, product
                identifiers, purchase dates, and your entitlement status. We use
                this to validate purchases, unlock access, provide customer
                support, and process refunds or revocations.
              </li>
            </ul>
          </div>

          <div className='ml-5 mt-4'>
            <h3 className='text-xl font-semibold'>
              1.5 Automatically Collected Data
            </h3>
            <p className='text-gray-600 mt-2'>
              When you access the Service, we automatically collect:
            </p>
            <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
              <li>
                <b>Device & Usage Data:</b> IP addresses, device identifiers,
                operating system, browser type, pages viewed, app interactions,
                crash logs, and diagnostic data.
              </li>
              <li>
                <b>Cookies & Similar Technologies:</b> We use a limited set of
                cookies, local storage, and similar technologies necessary to
                keep you signed in, remember your preferences, and understand
                basic app performance. We do not use them for advertising or
                cross-site tracking. If our use of these technologies changes
                materially, we will update this Policy accordingly.
              </li>
            </ul>
          </div>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            2. How We Use Your Information
          </h2>
          <p className='text-gray-600 mt-2'>
            We process personal information for the following purposes:
          </p>
          <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
            <li>
              <b>Service Delivery:</b> To create your account, manage your
              properties, track rent and expenses, generate reminders,
              summaries, and tax reports, and provide localized recommendations.
            </li>
            <li>
              <b>Purchases & Access:</b> To validate purchases with the App
              Store or Google Play, grant and manage your access and
              entitlements, and provide related customer support.
            </li>
            <li>
              <b>Automated Communications:</b> To facilitate property management
              by sending automated rent-due reminders, maintenance alerts,
              expense reminders, and rent receipts to you and, where applicable,
              your tenants, via email or in-app push notifications.
            </li>
            <li>
              <b>Account Management:</b> To send administrative, transactional,
              and security notices.
            </li>
            <li>
              <b>Contacting You:</b> We may contact the landlord account holder
              about reminders, service updates, or occasionally to ask about
              your experience with the Service so we can improve it. We do not
              use your contact information to send promotional or marketing
              messages, and we do not send marketing to tenants.
            </li>
            <li>
              <b>Improvement & Security:</b> To analyze usage trends, debug
              software issues, conduct security testing, and prevent fraud.
            </li>
            <li>
              <b>Compliance:</b> To enforce our Terms of Service and comply with
              legal or regulatory obligations.
            </li>
          </ul>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            3. Information Sharing and Disclosure
          </h2>
          <p className='text-gray-600 mt-2'>
            We do not sell or rent your personal information to third parties.
            We only share your data in the following circumstances:
          </p>
          <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
            <li>
              <b>App Stores (Payment Processing):</b> The Apple App Store and
              Google Play process your purchases and provide us with purchase
              and receipt information so we can validate transactions and manage
              your access. Their handling of your payment information is
              governed by their own privacy policies.
            </li>
            <li>
              <b>Service Providers:</b> We may share data with trusted
              third-party vendors who provide cloud hosting, database
              management, email delivery, and analytics services. These
              providers are strictly bound by confidentiality agreements.
            </li>
            <li>
              <b>Third-Party Services (User Initiated):</b> If you choose to
              interact with local services recommended in the app, any
              information you provide directly to them is governed by their
              respective privacy policies.
            </li>
            <li>
              <b>Legal Requirements:</b> We may disclose your information if
              required by law, subpoena, or court order, or if we believe
              disclosure is necessary to protect our rights, your safety, or the
              safety of others.
            </li>
            <li>
              <b>Business Transfers:</b> In the event of a merger, acquisition,
              or sale of assets, user data may be transferred to the acquiring
              entity under the same privacy commitments.
            </li>
          </ul>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            4. Legal Grounds for Processing & CASL Compliance
          </h2>
          <p className='text-gray-600 mt-2'>
            Under <b>PIPEDA</b>, we rely on your explicit and implied consent to
            process your data for the reasonable fulfillment of the Service.
          </p>
          <p className='text-gray-600 mt-2'>
            Under <b>Canada's Anti-Spam Legislation (CASL)</b>, the automated
            emails triggered by the app (such as rent receipts, expense
            reminders, and maintenance reminders) are classified as
            transactional, non-commercial electronic messages. They do not
            require separate express consent provided they contain no marketing
            material.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            5. Landlord Obligations & Tenant Consent
          </h2>
          <p className='text-gray-600 mt-2'>
            If you are a landlord or property manager entering a tenant's
            personal information into the HomeOwner app,{" "}
            <b>
              you represent and warrant that you have obtained meaningful,
              explicit consent
            </b>{" "}
            from the tenant to do so. You must inform your tenant:
          </p>
          <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
            <li>
              What data is being collected (name, contact info, financial lease
              details).
            </li>
            <li>
              That their data will be uploaded to and processed by Arptix
              Solutions Inc. via the HomeOwner app to facilitate management,
              automated reminders, and receipt generation.
            </li>
            <li>
              How they can request the deletion of their data (by contacting
              you, the landlord).
            </li>
          </ul>
          <p className='text-gray-600 mt-2'>
            Failure to secure tenant consent may result in the suspension of
            your account.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            6. Data Security and Retention
          </h2>
          <p className='text-gray-600 mt-2'>
            We employ industry-standard security measures, including:
          </p>
          <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
            <li>
              TLS encryption for data in transit and AES-256 encryption for data
              at rest.
            </li>
            <li>
              Role-based access controls and least-privilege principles for our
              personnel.
            </li>
            <li>Regular security testing and vulnerability assessments.</li>
          </ul>
          <p className='text-gray-600 mt-4'>
            <b>Retention:</b> We retain your account data for as long as your
            account exists, including during any period when your free trial has
            ended or your access is locked pending purchase. Locking access does
            not delete your data; your information is preserved so it is
            available to you if you choose to unlock the app later.
          </p>
          <p className='text-gray-600 mt-2'>
            <b>Account Deletion:</b> You may delete your account at any time via
            the app settings. Upon deletion, we permanently erase your personal
            and property data, including your profile, properties, tenant
            records, documents, expense records, and journey data. This deletion
            is irreversible.
          </p>
          <p className='text-gray-600 mt-2'>
            The only exception is a limited financial record of any purchases
            you made (such as the transaction identifier, amount, date, and
            product purchased), which we retain in a form stripped of your
            personal details. We keep this minimal record because tax,
            accounting, and consumer protection laws require us to retain
            records of completed sales, and because it allows us to handle any
            later refund or dispute. This record does not include your name,
            contact information, or any property or tenant data.
          </p>
          <p className='text-gray-600 mt-2'>
            <b>Property/Tenant Deletion:</b> You can delete individual
            properties or tenant records at any time without deleting your
            primary account.
          </p>
          <p className='text-gray-600 mt-2'>
            <b>Purchase Records:</b> We retain purchase and transaction records
            as needed to provide support, process refunds, and meet accounting,
            tax, and legal obligations, even after an item is refunded.
          </p>
          <p className='text-gray-600 mt-2'>
            <b>System Logs:</b> Diagnostic and usage logs are retained for a
            maximum of 24 months before being aggregated, anonymized, or
            deleted.
          </p>
          <p className='text-gray-600 mt-2'>
            <i>
              Note: While we strive to protect your data, no digital
              transmission or storage is 100% secure, and we cannot guarantee
              absolute security.
            </i>
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            7. Your Rights and Choices
          </h2>
          <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
            <li>
              <b>Access & Correction:</b> You can review, update, and correct
              your personal information directly within your app settings.
            </li>
            <li>
              <b>Data Deletion:</b> You may erase your account or specific
              records at any time.
            </li>
            <li>
              <b>Communication Preferences:</b> You can toggle off automated
              reminders for specific tenants within the property settings.
            </li>
            <li>
              <b>Tenant Rights:</b> If you are a tenant whose data is stored in
              the app by a landlord, you must contact your landlord directly to
              access, correct, or delete your information. If the landlord fails
              to comply, you may contact us for assistance.
            </li>
          </ul>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            8. International Data Transfers
          </h2>
          <p className='text-gray-600 mt-2'>
            Our primary servers are located in Canada. However, some of our
            third-party service providers may process data in the United States
            or other jurisdictions. By using the Service, you consent to the
            transfer and processing of your data outside your country of
            residence, where it may be subject to access by local law
            enforcement agencies under applicable local laws.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            9. Children's Privacy
          </h2>
          <p className='text-gray-600 mt-2'>
            Account holders must be adults (18 years of age or older), because
            using the Service involves entering into a purchase agreement and
            handling personal and financial information. This is a requirement
            of account eligibility and legal capacity, and is separate from any
            app store content rating. We do not knowingly collect personal data
            from children. If we become aware that we have inadvertently
            collected such data, we will take immediate steps to delete it.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            10. Changes to This Policy
          </h2>
          <p className='text-gray-600 mt-2'>
            We may update this Privacy Policy periodically to reflect changes in
            our practices or legal requirements. The updated policy will be
            available within the app, and the "Last Updated" date will be
            revised. For material changes, we will provide an in-app
            notification or send an email to the address associated with your
            account.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            11. Contact Us
          </h2>
          <p className='text-gray-600 mt-2'>
            If you have any questions, concerns, or privacy requests regarding
            this Privacy Policy or Arptix Solutions Inc.'s data practices,
            please contact our Privacy Officer at:
          </p>
          <p className='text-gray-600 mt-2'>
            <b>Email:</b>{" "}
            <a
              href='mailto:info@thehomeowner.ca'
              className='text-blue-600 hover:underline'>
              info@thehomeowner.ca
            </a>
            <br />
            Arptix Solutions Inc.
          </p>
        </section>
      </div>
      <BottomSection />
    </div>
  );
};

export default PrivacyPolicy;
